import { Modal, Table } from 'antd';
import Column from 'antd/lib/table/Column';
import React, { useMemo, useState } from 'react';
import SubscriptionPlans from './SubscriptionPlans';
import { useFetchSubscriptionPlansQuery } from '../service';
import LoadingIndicator from '../../components/shared/LoadingIndicator/LoadingIndicator';
import { getPlanDetails } from '../utils';
import moment from 'moment';

type TableRecord = {
    key: string;
    name: string;
    value: string | number;
};

type Props = {
    selectedPlanCode: string | undefined;
    isInFreeTrialSetup?: boolean;
    onSelectPlan?: (planCode: string) => void;
};

export default function SelectedPlanDetails({ selectedPlanCode, isInFreeTrialSetup, onSelectPlan = () => {} }: Props) {
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const { data: plans, isLoading: isLoadingPlans } = useFetchSubscriptionPlansQuery();

    const selectedPlan = useMemo(() => (plans && selectedPlanCode ? getPlanDetails(plans, selectedPlanCode) : undefined), [plans, selectedPlanCode]);

    const handlePlanSelected = (planCode: string) => {
        setIsModalVisible(false);
        onSelectPlan(planCode);
    };

    const tableDataSource = useMemo(
        () =>
            selectedPlan
                ? [
                      {
                          key: '1',
                          name: 'Account type',
                          value: selectedPlan.name,
                      },
                      {
                          key: '2',
                          name: 'Price',
                          value: `$${selectedPlan.price / 100}`,
                      },
                      {
                          key: '3',
                          name: 'Billing period',
                          value: selectedPlan.billing_period.charAt(0).toUpperCase() + selectedPlan.billing_period.slice(1),
                      },
                      {
                          key: '4',
                          name: 'Starting date',
                          value: isInFreeTrialSetup ? moment().add(7, 'd').format('MMM DD, YYYY') : moment().format('MMM DD, YYYY'),
                      },
                  ]
                : [],
        [isInFreeTrialSetup, selectedPlan]
    );

    return (
        <>
            {(isLoadingPlans || !selectedPlan) && <LoadingIndicator />}

            {!isLoadingPlans && selectedPlan && (
                <>
                    <Table
                        className={`selected-plan-table ${isInFreeTrialSetup ? 'free-trial' : ''}`}
                        dataSource={tableDataSource}
                        size="small"
                        pagination={false}
                        title={() =>
                            isInFreeTrialSetup &&
                            `Free for 7 days then $${selectedPlan.price / 100} for each ${selectedPlan.interval_length === 1 ? 'month' : 'year'}`
                        }
                    >
                        <Column
                            className="plan-prop-name"
                            dataIndex="name"
                            key="name"
                        />

                        <Column
                            className="plan-prop-value"
                            dataIndex="value"
                            key="value"
                            render={(text, record: TableRecord) =>
                                isInFreeTrialSetup && record.name === 'Account type' ? (
                                    <span>
                                        {text}{' '}
                                        <span
                                            className="change-plan"
                                            onClick={() => setIsModalVisible(true)}
                                        >
                                            (change plan)
                                        </span>
                                    </span>
                                ) : (
                                    record.value
                                )
                            }
                        />
                    </Table>

                    <Modal
                        className="subscription-plans-modal"
                        title="Choose your free trial plan"
                        visible={isModalVisible}
                        onCancel={() => setIsModalVisible(false)}
                        footer={null}
                        destroyOnClose
                        width="1000px"
                    >
                        <SubscriptionPlans
                            selectedPlanCode={selectedPlanCode!}
                            isInFreeTrialSetup={isInFreeTrialSetup}
                            onSelectSubscription={handlePlanSelected}
                        />
                    </Modal>
                </>
            )}
        </>
    );
}
